import { useId, useState, type ChangeEvent, type FormEvent } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react/ssr'
import { ModalDialog } from '../modal-dialog/ModalDialog'
import { EmptyState } from '../empty-state/EmptyState'
import FetchError from '../../FetchError'
import { toSaveErrorMessage } from '../../utils/userErrorUtils'
import {
  type CreateLabReceptionAddress,
  type LabReceptionAddress
} from '../../types/types'
import {
  useCreateLabReceptionAddressMutation,
  useDeleteLabReceptionAddressMutation,
  useGetLabReceptionAddressesQuery,
  useUpdateLabReceptionAddressMutation
} from '../../hooks/useLabReceptionAddresses'

type Props = {
  labId: number
  title?: string | null
}

type FormState = Omit<CreateLabReceptionAddress, 'isDefault'> & {
  isDefault: boolean
}

const emptyForm: FormState = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  countryCode: 'NO',
  isDefault: false
}

const toFormState = (address: LabReceptionAddress): FormState => ({
  name: address.name,
  addressLine1: address.addressLine1,
  addressLine2: address.addressLine2 ?? '',
  postalCode: address.postalCode,
  city: address.city,
  countryCode: address.countryCode,
  isDefault: address.isDefault
})

export const LabReceptionAddressesSection = ({
  labId,
  title = 'Mottaksadresser'
}: Props) => {
  const {
    data: addresses = [],
    isLoading,
    isSuccess,
    isError,
    isFetching,
    refetch
  } = useGetLabReceptionAddressesQuery(labId)
  const createAddress = useCreateLabReceptionAddressMutation(labId)
  const updateAddress = useUpdateLabReceptionAddressMutation(labId)
  const deleteAddress = useDeleteLabReceptionAddressMutation(labId)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [editing, setEditing] = useState<LabReceptionAddress | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] =
    useState<LabReceptionAddress | null>(null)
  const [conflict, setConflict] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const baseId = useId()
  const dialogId = `lab-reception-address-form-${baseId}`
  const menuId = (addressId: number) =>
    `lab-reception-address-menu-${baseId}-${addressId}`

  const reset = () => {
    setEditing(null)
    setForm(emptyForm)
    setConflict(false)
    setMutationError(null)
    setFormOpen(false)
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setConflict(false)
    setMutationError(null)
    setConfirmDelete(null)
    setFormOpen(true)
  }

  const openEdit = (address: LabReceptionAddress) => {
    setEditing(address)
    setForm(toFormState(address))
    setConflict(false)
    setMutationError(null)
    setConfirmDelete(null)
    setFormOpen(true)
  }

  const setField = (field: keyof FormState, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (conflict || !isSuccess) return
    setMutationError(null)
    const body = {
      ...form,
      isDefault: form.isDefault || addresses.length === 0,
      addressLine2: form.addressLine2 || undefined,
      countryCode: form.countryCode.trim().toUpperCase()
    }
    try {
      if (editing) {
        await updateAddress.mutateAsync({
          addressId: editing.id,
          etag: editing.etag,
          body
        })
      } else {
        await createAddress.mutateAsync(body)
      }
      reset()
    } catch (error) {
      if (error instanceof FetchError && error.status === 412) {
        setConflict(true)
      } else {
        setMutationError(toSaveErrorMessage(error, 'lagre adressen'))
      }
    }
  }

  const reloadEditing = async () => {
    const result = await refetch()
    if (!result.isSuccess) return
    const latest = result.data.find((address) => address.id === editing?.id)
    if (!latest) {
      setMutationError(
        'Adressen er slettet. Utkastet er beholdt. Avbryt redigeringen for å fortsette.'
      )
      return
    }
    setEditing(latest)
    setForm(toFormState(latest))
    setConflict(false)
    setMutationError(null)
  }

  const remove = async (address: LabReceptionAddress) => {
    setMutationError(null)
    try {
      await deleteAddress.mutateAsync({
        addressId: address.id,
        etag: address.etag
      })
      if (editing?.id === address.id) reset()
    } catch (error) {
      setMutationError(
        error instanceof FetchError && error.status === 412
          ? 'Adressen er endret av en annen bruker og ble ikke slettet. Kontroller den oppdaterte listen før du prøver igjen.'
          : error instanceof FetchError && error.status === 409
            ? 'Adressen brukes av oppgaver og kan ikke slettes. Velg en annen mottaksadresse på oppgavene først.'
            : 'Kunne ikke slette adressen. Prøv igjen.'
      )
    } finally {
      setConfirmDelete(null)
    }
  }

  const isPending =
    createAddress.isPending ||
    updateAddress.isPending ||
    deleteAddress.isPending
  const disabled = isPending || !isSuccess || isFetching
  const isNorwegian = form.countryCode.trim().toUpperCase() === 'NO'

  return (
    <Mtds.Grid data-gap='5'>
      <Mtds.Flex data-align='center' data-justify='space-between' data-gap='4'>
        {title && (
          <Mtds.Heading as='h3' data-size='sm'>
            {title}
          </Mtds.Heading>
        )}
        <Mtds.Button
          data-variant='primary'
          className='ml-auto shrink-0'
          disabled={disabled}
          onClick={openCreate}
        >
          Legg til mottaksadresse
        </Mtds.Button>
      </Mtds.Flex>
      <p>Adresser som kan brukes for levering av prøver til laboratoriet.</p>
      {isLoading && <p>Laster mottaksadresser...</p>}
      {isError && (
        <Mtds.Alert as='div' role='alert' data-color='danger'>
          <p>Kunne ikke laste mottaksadressene.</p>
          <Mtds.Button disabled={isFetching} onClick={() => void refetch()}>
            Prøv igjen
          </Mtds.Button>
        </Mtds.Alert>
      )}
      {isSuccess && addresses.length === 0 && (
        <EmptyState
          title='Ingen mottaksadresser'
          description='Det er ikke registrert noen mottaksadresser for laboratoriet.'
        />
      )}
      {mutationError && !formOpen && (
        <Mtds.Alert as='div' role='alert' data-color='danger'>
          {mutationError}
        </Mtds.Alert>
      )}
      <Mtds.Grid data-gap='3'>
        {addresses.map((address) => (
          <Mtds.Card key={address.id} data-pad='4'>
            <Mtds.Grid data-gap='3'>
              <Mtds.Flex
                data-align='center'
                data-justify='space-between'
                data-gap='4'
              >
                <Mtds.Grid data-gap='1' className='min-w-0'>
                  <Mtds.Heading as='h4' data-size='xs'>
                    {address.name}
                  </Mtds.Heading>
                  <Mtds.Muted>
                    {[
                      address.addressLine1,
                      address.addressLine2,
                      `${address.postalCode} ${address.city}`,
                      address.countryCode
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Mtds.Muted>
                </Mtds.Grid>
                <Mtds.Flex data-align='center' data-fixed data-nowrap>
                  {address.isDefault && (
                    <Mtds.Tag data-color='neutral'>Standard</Mtds.Tag>
                  )}
                  <Mtds.Button
                    type='button'
                    disabled={disabled}
                    aria-label={`Handlingsmeny for ${address.name}`}
                    popoverTarget={menuId(address.id)}
                  />
                  <Mtds.Popover as='menu' id={menuId(address.id)}>
                    <li>
                      <Mtds.Button
                        popoverTargetAction='hide'
                        aria-label={`Rediger ${address.name}`}
                        onClick={() => openEdit(address)}
                      >
                        <PencilSimpleIcon />
                        Rediger
                      </Mtds.Button>
                    </li>
                    <li>
                      <Mtds.Button
                        type='button'
                        data-color='danger'
                        popoverTargetAction='hide'
                        aria-label={`Slett ${address.name}`}
                        onClick={() => setConfirmDelete(address)}
                      >
                        <TrashIcon />
                        Slett
                      </Mtds.Button>
                    </li>
                  </Mtds.Popover>
                </Mtds.Flex>
              </Mtds.Flex>
              {confirmDelete?.id === address.id && (
                <Mtds.Alert as='div' data-color='warning'>
                  <p>Vil du slette mottaksadressen {address.name}?</p>
                  {address.isDefault && addresses.length > 1 && (
                    <p>En annen adresse blir automatisk standardadresse.</p>
                  )}
                  <Mtds.Flex>
                    <Mtds.Button
                      data-color='danger'
                      disabled={disabled}
                      onClick={() => void remove(confirmDelete)}
                    >
                      Bekreft sletting
                    </Mtds.Button>
                    <Mtds.Button
                      disabled={isPending}
                      onClick={() => setConfirmDelete(null)}
                    >
                      Avbryt sletting
                    </Mtds.Button>
                  </Mtds.Flex>
                </Mtds.Alert>
              )}
            </Mtds.Grid>
          </Mtds.Card>
        ))}
      </Mtds.Grid>
      <ModalDialog
        id={dialogId}
        open={formOpen}
        closedby={isPending ? 'none' : 'closerequest'}
        aria-labelledby={`${dialogId}-title`}
        onClose={() => {
          if (!isPending) reset()
        }}
        onCancel={(event) => {
          event.preventDefault()
          if (!isPending) reset()
        }}
      >
        <Mtds.Button
          type='button'
          command='request-close'
          commandfor={dialogId}
          aria-label='Lukk'
          disabled={isPending}
          onClick={reset}
        />
        <Mtds.Grid
          as='form'
          onSubmit={save}
          data-gap='4'
          data-validation='form'
          aria-label='Mottaksadresse'
        >
          <Mtds.Heading as='h4' data-size='sm' id={`${dialogId}-title`}>
            {editing ? 'Rediger mottaksadresse' : 'Legg til mottaksadresse'}
          </Mtds.Heading>
          {mutationError && (
            <Mtds.Alert as='div' role='alert' data-color='danger'>
              {mutationError}
            </Mtds.Alert>
          )}
          {conflict && (
            <Mtds.Alert as='div' role='alert' data-color='warning'>
              <p>
                Adressen er endret av en annen bruker. Utkastet ditt er beholdt
                og er ikke lagret. Sammenlign med adressen i listen, eller last
                inn siste versjon før du redigerer videre.
              </p>
              <Mtds.Button
                type='button'
                disabled={isFetching}
                onClick={() => void reloadEditing()}
              >
                Last inn siste versjon og forkast utkast
              </Mtds.Button>
            </Mtds.Alert>
          )}
          <Mtds.Field
            as='input'
            label='Navn'
            disabled={disabled}
            required
            value={form.name}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setField('name', event.currentTarget.value)
            }
          />
          <Mtds.Field
            as='input'
            label='Adresselinje 1'
            disabled={disabled}
            required
            value={form.addressLine1}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setField('addressLine1', event.currentTarget.value)
            }
          />
          <Mtds.Field
            as='input'
            label='Adresselinje 2'
            disabled={disabled}
            value={form.addressLine2}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setField('addressLine2', event.currentTarget.value)
            }
          />
          <Mtds.Grid data-gap='4'>
            <Mtds.Field
              as='input'
              label='Postnummer'
              type='text'
              disabled={disabled}
              inputMode={isNorwegian ? 'numeric' : 'text'}
              maxLength={isNorwegian ? 4 : undefined}
              pattern={isNorwegian ? '[0-9]{4}' : undefined}
              description={
                isNorwegian ? 'Fire sifre, for eksempel 0150.' : undefined
              }
              required
              value={form.postalCode}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setField('postalCode', event.currentTarget.value)
              }
            />
            <Mtds.Field
              as='input'
              label='Poststed'
              disabled={disabled}
              required
              value={form.city}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setField('city', event.currentTarget.value)
              }
            />
            <Mtds.Field
              as='input'
              label='Landkode'
              description='ISO-landkode, for eksempel NO, SE eller GB.'
              disabled={disabled}
              required
              maxLength={2}
              pattern='[A-Za-z]{2}'
              value={form.countryCode}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setField('countryCode', event.currentTarget.value)
              }
            />
          </Mtds.Grid>
          <Mtds.Field
            as='input'
            label='Standardadresse'
            description={
              editing?.isDefault
                ? 'Velg en annen adresse som standard for å bytte.'
                : addresses.length === 0
                  ? 'Den første adressen blir automatisk standardadresse.'
                  : undefined
            }
            disabled={disabled || editing?.isDefault || addresses.length === 0}
            type='checkbox'
            checked={form.isDefault || addresses.length === 0}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setField('isDefault', event.currentTarget.checked)
            }
          />
          <Mtds.Flex data-justify='end'>
            <Mtds.Button
              type='button'
              onClick={reset}
              disabled={isPending || isFetching}
            >
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              disabled={disabled || conflict}
            >
              {editing ? 'Lagre endringer' : 'Legg til adresse'}
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Grid>
      </ModalDialog>
    </Mtds.Grid>
  )
}
